import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RpcError, StatusCode } from 'grpc-web';
import { API_URL, getCurrentLng, getHeaders, ApiMessage, toApiMessage, APIRequest, APIResponse, refreshSession, getSession } from '../../../../app/Api';
import { formatDate, timestampToDate } from '../../../../app/Helpers';

import { TableState } from '../../../common/Table/TableSate';
import {   DeleteRoleResponse, DeleteRoleRequest  } from '../../../../repository/UserManagement/role_pb';
import { GetRolesRequest, GetRolesResponse } from '../../../../repository/UserManagement/organization_pb';
import { RoleEndpointClient } from '../../../../repository/UserManagement/RoleServiceClientPb';
import { number } from 'yup';
import { OrganizationEndpointClient } from '../../../../repository/UserManagement/OrganizationServiceClientPb';


export interface RolesPageState {
    roles: TableState
    isChangeStateLoading: boolean,
    message?: ApiMessage,
    searchText: string | undefined,
    filters: any[],

}

const initialState: RolesPageState = {
    roles: { items: [], hasMore: true, isDescending: true, isFetching: false, numberOfResults: 10, selected: [], isFilteredSet: false },
    isChangeStateLoading: false,
    message: undefined,
    searchText: undefined,
    filters: []
}

const clientRole = new RoleEndpointClient(API_URL, null, null);
const clientOrg = new OrganizationEndpointClient(API_URL, null, null);


export const getRoles = createAsyncThunk<GetRolesResponse.AsObject, APIRequest<GetRolesRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/customers/roles/getRoles',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {
                var r = await clientOrg.getRoles(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }

)


export const deleteRole = createAsyncThunk<APIResponse<DeleteRoleResponse.AsObject>, APIRequest<DeleteRoleRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/customers/roles/delete',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {
                var r = await clientRole.delete(req.body, req.headers ?? {});
                return { metadata: { id: req.body.getId() }, response: r.toObject() };
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }
)


const getFilters = (state: RolesPageState): any[] => {

    return [];

}

export const customersRolesPageSlice = createSlice({
    name: 'pages/customers/roles',
    initialState,
    reducers: {
        dismissMessage: (state) => {
            state.message = undefined;
        },
        reset: (state) => {
            state.message = initialState.message;
            state.roles = initialState.roles;
            state.isChangeStateLoading = initialState.isChangeStateLoading;
            state.searchText = initialState.searchText;
        },
        resetRoles: (state) => {
            state.roles.items = [];
            state.roles.hasMore = true;
        },

        setSort: (state, action: PayloadAction<boolean>) => {
            state.roles.isDescending = action.payload;
        },
        setNumberOfResults: (state, action: PayloadAction<number>) => {
            state.roles.numberOfResults = action.payload;
        },
        setSelectedRole: (state, action: PayloadAction<any[]>) => {
            state.roles.selected = action.payload;
        },
        setSearchText: (state, action: PayloadAction<string | undefined>) => {
            state.searchText = action.payload;
        },
        addRole: (state, action: PayloadAction<any>) => {
            if (!state.roles.isFilteredSet) {
                if (!state.roles.hasMore && !state.roles.isDescending) {
                    state.roles.items.push(action.payload);
                } else if (state.roles.isDescending) {
                    state.roles.items.unshift(action.payload);
                }
            }
        },
        updateRole: (state, action: PayloadAction<any>) => {
            let selected = state.roles.selected;
            if (action?.payload) {
                if (selected.length > 0 && selected.at(0).id == action?.payload?.id) {
                    selected[0] = action?.payload
                }
                var u = state.roles.items.findIndex(e => e.id == action?.payload?.id)
                if (u >= 0) {
                    state.roles.items[u] = action?.payload
                }
            }

        },
        setIsFilteredSet: (state, action: PayloadAction<boolean>) => {
            state.roles.isFilteredSet = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getRoles.fulfilled, (state, { payload }) => {
            state.roles.isFetching = false
            if (payload) {
                var r = (payload?.success?.rolesList.map(val => {
                    return {
                        id: val.id,
                        name: val.name?.value ?? "",
                        isDefault: val.isdefault,
                        roleCategory: val.rolecategory,
                        createdDate: formatDate(timestampToDate(val.createddate?.seconds, val.createddate?.nanos)),
                        addedBy: (val.addedby?.firstname?.value ?? "") + " " + (val.addedby?.lastname?.value ?? ""),
                        addedById: val.addedby?.id?.value,
                    }
                }
                ) as any[])
                //r = (r.concat(...Array(9).fill(r)));
                let l = [];
                if (state.roles.items.length != 0) {
                    l = state.roles.items.slice(0, state.roles.items.length - 10)
                }
                l = l.concat(r);
                if (r.length < state.roles.numberOfResults) {
                    state.roles.hasMore = false;
                } else {
                    l = l.concat(...Array(10).fill(null));
                }

                state.roles.items = l
                //console.log(state.items)

            } else {
                state.roles.hasMore = false;
            }
        })
        builder.addCase(getRoles.rejected, (state, action) => {
            state.roles.isFetching = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(getRoles.pending, (state, action) => {
            state.roles.isFetching = true;
            state.message = undefined;
        })



        builder.addCase(deleteRole.fulfilled, (state, { payload }) => {
            state.isChangeStateLoading = false;
            if (payload) {
                state.message = toApiMessage(payload?.response.success?.message);
                let selected = state.roles.selected;
                if (payload?.metadata) {
                    if (selected.length > 0 && selected.at(0).id == payload?.metadata.id) {
                        state.roles.selected = []
                    }
                    var u = state.roles.items.findIndex(e => e.id == payload?.metadata.id)
                    if (u >= 0) {
                        state.roles.items.splice(u, 1);
                    }
                }
            }
        })
        builder.addCase(deleteRole.rejected, (state, action) => {
            state.isChangeStateLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(deleteRole.pending, (state, action) => {
            state.isChangeStateLoading = true;
            state.message = undefined;
        })




    }
})

export const { reset, dismissMessage, setSort, setNumberOfResults, resetRoles, setSelectedRole, setSearchText, addRole, setIsFilteredSet, updateRole } = customersRolesPageSlice.actions

export default customersRolesPageSlice.reducer


